'use client';

import { createContext, useContext, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';

interface ScrollManagerContextValue {
  saveScroll: (key: string, position: number) => void;
  getScroll: (key: string) => number;
  clearScroll: (key: string) => void;
  clearAll: () => void;
}

const ScrollManagerContext = createContext<ScrollManagerContextValue | undefined>(undefined);

export const ScrollManagerProvider = ({ children }: { children: ReactNode }) => {
  const scrollPositionsRef = useRef<Map<string, number>>(new Map());

  const saveScroll = useCallback((key: string, position: number) => {
    scrollPositionsRef.current.set(key, position);
  }, []);

  const getScroll = useCallback((key: string): number => {
    return scrollPositionsRef.current.get(key) ?? 0;
  }, []);

  const clearScroll = useCallback((key: string) => {
    scrollPositionsRef.current.delete(key);
  }, []);

  const clearAll = useCallback(() => {
    scrollPositionsRef.current.clear();
  }, []);

  return (
    <ScrollManagerContext.Provider value={{ saveScroll, getScroll, clearScroll, clearAll }}>
      {children}
    </ScrollManagerContext.Provider>
  );
};

const NO_OP: ScrollManagerContextValue = {
  saveScroll: () => {},
  getScroll: () => 0,
  clearScroll: () => {},
  clearAll: () => {},
};

export const useScrollManager = (): ScrollManagerContextValue => {
  return useContext(ScrollManagerContext) ?? NO_OP;
};
